import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import {
  MenuComponent,
  MenuEventArgs,
  MenuItemModel,
} from '@syncfusion/ej2-react-navigations'
import { InsurancePlan } from '@dataTypes/insurance.types'
import { Discount } from '@dataTypes/discount.type'
import { ITreatmentPlan } from '@dataTypes/treatmentPlan.types'
import { TX_PLAN_DISCOUNT_CUSTOM_ID } from '@utils/treatment-plan.utils'
import type { TxPlanProviderMenuAvailability } from '../TreatmentPlanMain.types'
import './PlanActionMenuButton.css'

export type PlanProviderMenuAction =
  | 'assign-missing'
  | 'replace-all'
  | 'clear-all'

type PlanActionSubmenuKind =
  | 'duplicate'
  | 'insurance'
  | 'discount'
  | 'providers'

const isPlanActionSubmenuPopup = (popupElement: HTMLElement): boolean =>
  popupElement.querySelector('[id^="duplicate-plan-"]') !== null ||
  popupElement.querySelector('[id^="insurance-"]') !== null ||
  popupElement.querySelector('[id^="discount-"]') !== null ||
  popupElement.querySelector('#discount-custom') !== null ||
  popupElement.querySelector('[id^="provider-menu-"]') !== null

const DEFAULT_PROVIDER_MENU_DISABLED: TxPlanProviderMenuAvailability = {
  assignMissingDisabled: true,
  replaceAllDisabled: true,
  clearAllDisabled: true,
}

const formatBillingDiscountPercent = (value: number): string => {
  const n = Number(value)
  if (Number.isNaN(n)) {
    return '0'
  }
  return String(parseFloat(n.toFixed(10)))
}

interface PlanActionMenuButtonProps {
  tabKey: string
  isLocalDraft: boolean
  insuranceList: InsurancePlan[]
  selectedInsurance: InsurancePlan | null
  billingDiscounts: Discount[]
  selectedDiscountId: string
  customDiscount: number
  treatmentPlans: ITreatmentPlan[]
  onMenuSelect: (value: string, tabKey: string) => void
  onInsuranceSelect: (carrier: string, tabKey: string) => void
  onDiscountSelect: (discountId: string, tabKey: string) => void
  onCustomDiscountChange: (value: number, tabKey: string) => void
  onDuplicatePlan?: (sourcePlanId: string) => void
  providerMenuDisabled?: TxPlanProviderMenuAvailability
  onProviderMenuSelect?: (
    action: PlanProviderMenuAction,
    tabKey: string,
  ) => void
}

const PlanActionMenuButton: React.FC<PlanActionMenuButtonProps> = ({
  tabKey,
  isLocalDraft,
  insuranceList,
  selectedInsurance,
  billingDiscounts,
  selectedDiscountId,
  customDiscount,
  treatmentPlans,
  onMenuSelect,
  onInsuranceSelect,
  onDiscountSelect,
  onCustomDiscountChange,
  onDuplicatePlan,
  providerMenuDisabled = DEFAULT_PROVIDER_MENU_DISABLED,
  onProviderMenuSelect,
}): JSX.Element => {
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false)
  const [menuPosition, setMenuPosition] = useState<{
    top: number
    left: number
  }>({
    top: 0,
    left: 0,
  })
  const [showCustomDiscountInput, setShowCustomDiscountInput] =
    useState<boolean>(false)
  const [customDiscountValue, setCustomDiscountValue] = useState<string>('')
  const containerRef = useRef<HTMLDivElement>(null)
  const portalRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<MenuComponent>(null)
  const DEFAULT_MENU_WIDTH = 180
  const DEFAULT_MENU_HEIGHT = 200
  const MENU_GAP = 2

  // Extract unique carriers from insurance list
  const getUniqueCarriers = (): string[] => {
    const carriers = insuranceList
      .map((plan) => plan.carrier_name)
      .filter((carrier): carrier is string => Boolean(carrier))
    return Array.from(new Set(carriers))
  }

  // Create menu items with submenu for insurance and discount
  const createMenuItems = (): MenuItemModel[] => {
    const items: MenuItemModel[] = []

    // 1. Add Duplicate plan submenu FIRST
    if (treatmentPlans.length > 0 && onDuplicatePlan) {
      const duplicateItems: MenuItemModel[] = treatmentPlans.map((plan) => {
        const planName = plan.tp_heading || 'Untitled Plan'
        return {
          text: planName,
          id: `duplicate-plan-${plan.tp_id}`,
        }
      })

      items.push({
        text: 'Duplicate plan',
        id: 'select-duplicate',
        items: duplicateItems,
      })
    }

    // 2. Add Select Insurance with submenu for carriers
    const carriers = getUniqueCarriers()
    if (carriers.length > 0) {
      items.push({
        text: 'Insurance',
        id: 'select-insurance',
        items: carriers.map((carrier) => {
          const isSelected = selectedInsurance?.carrier_name === carrier
          const menuItem: MenuItemModel = {
            text: carrier,
            id: `insurance-${carrier}`,
          }
          if (isSelected) {
            menuItem.iconCss = 'ti ti-check insurance-check-icon'
          }
          return menuItem
        }),
      })
    }

    // 3. Add Discount submenu
    const discountMenuItems: MenuItemModel[] = [
      {
        text: 'No Discount',
        id: 'discount-none',
        iconCss:
          selectedDiscountId === ''
            ? 'ti ti-check discount-check-icon'
            : undefined,
      },
    ]

    billingDiscounts.forEach((disc) => {
      const pct = formatBillingDiscountPercent(disc.discount_percentage)
      const isSelected = selectedDiscountId === disc.discount_id
      discountMenuItems.push({
        text: `${disc.description} ${pct}%`,
        id: `discount-preset:${disc.discount_id}`,
        iconCss: isSelected ? 'ti ti-check discount-check-icon' : undefined,
      })
    })

    const isCustomSelected = selectedDiscountId === TX_PLAN_DISCOUNT_CUSTOM_ID
    const customDiscountText =
      isCustomSelected && customDiscount > 0
        ? `Custom ${customDiscount}%`
        : 'Custom'
    discountMenuItems.push({
      text: customDiscountText,
      id: 'discount-custom',
      iconCss: isCustomSelected ? 'ti ti-check discount-check-icon' : undefined,
    })

    items.push({
      text: 'Discount',
      id: 'select-discount',
      items: discountMenuItems,
    })

    if (onProviderMenuSelect) {
      items.push({
        text: 'Providers',
        id: 'select-providers',
        items: [
          {
            text: 'Assign missing providers',
            id: 'provider-menu-assign',
          },
          {
            text: 'Replace all providers',
            id: 'provider-menu-replace',
          },
          {
            text: 'Clear all providers',
            id: 'provider-menu-clear',
          },
        ],
      })
    }

    // 4. Delete plan / Discard draft
    items.push({
      text: isLocalDraft ? 'Discard draft' : 'Delete plan',
      id: 'delete-plan',
    })

    return items
  }

  const menuItems = createMenuItems()

  // Handle menu item selection
  const handleMenuSelect = (args: MenuEventArgs): void => {
    const itemId = args.item?.id

    if (!itemId) return

    if (itemId === 'select-providers') {
      return
    }
    if (itemId === 'provider-menu-assign') {
      if (!providerMenuDisabled.assignMissingDisabled && onProviderMenuSelect) {
        onProviderMenuSelect('assign-missing', tabKey)
      }
      setMenuIsOpen(false)
    } else if (itemId === 'provider-menu-replace') {
      if (!providerMenuDisabled.replaceAllDisabled && onProviderMenuSelect) {
        onProviderMenuSelect('replace-all', tabKey)
      }
      setMenuIsOpen(false)
    } else if (itemId === 'provider-menu-clear') {
      if (!providerMenuDisabled.clearAllDisabled && onProviderMenuSelect) {
        onProviderMenuSelect('clear-all', tabKey)
      }
      setMenuIsOpen(false)
    } else if (itemId.startsWith('duplicate-plan-')) {
      const sourcePlanId = itemId.replace('duplicate-plan-', '')
      if (onDuplicatePlan) {
        onDuplicatePlan(sourcePlanId)
      }
      setMenuIsOpen(false)
    }
    // Check if it's an insurance carrier selection
    else if (itemId.startsWith('insurance-')) {
      const carrier = itemId.replace('insurance-', '')
      onInsuranceSelect(carrier, tabKey)
      setMenuIsOpen(false)
    } else if (itemId.startsWith('discount-')) {
      // Handle discount selection
      if (itemId === 'discount-custom') {
        // Show custom discount input - prevent menu from closing
        setShowCustomDiscountInput(true)
        setCustomDiscountValue(
          selectedDiscountId === TX_PLAN_DISCOUNT_CUSTOM_ID
            ? customDiscount.toString()
            : '0',
        )
        // Prevent default behavior to keep submenu open
        if (args.event) {
          args.event.preventDefault?.()
          args.event.stopPropagation?.()
        }
        // Keep the discount submenu open by ensuring the parent item stays expanded
        setTimeout(() => {
          const discountMenuItem = menuRef.current?.element?.querySelector(
            '#select-discount',
          ) as HTMLElement
          if (discountMenuItem) {
            discountMenuItem.setAttribute('aria-expanded', 'true')
            discountMenuItem.classList.add('e-focused')
          }
        }, 50)
        return // Don't proceed with normal menu handling
      } else if (itemId === 'discount-none') {
        onDiscountSelect('', tabKey)
        setShowCustomDiscountInput(false)
        setMenuIsOpen(false)
      } else if (itemId.startsWith('discount-preset:')) {
        const presetId = itemId.replace('discount-preset:', '')
        onDiscountSelect(presetId, tabKey)
        setShowCustomDiscountInput(false)
        setMenuIsOpen(false)
      }
    } else {
      // Handle other menu items
      onMenuSelect(itemId, tabKey)
      setMenuIsOpen(false)
    }
  }

  // Handle custom discount input change
  const handleCustomDiscountInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | Event): void => {
      const target =
        (e.target as HTMLInputElement) ||
        ((e as Event).target as HTMLInputElement)
      const value = target.value
      setCustomDiscountValue(value)
      const numValue = value === '' ? 0 : Number(value)
      onCustomDiscountChange(numValue, tabKey)
    },
    [onCustomDiscountChange, tabKey],
  )

  // Handle custom discount input blur
  const handleCustomDiscountInputBlur = useCallback((): void => {
    // If a value was entered, select custom discount and close menu
    if (customDiscountValue !== '' && Number(customDiscountValue) > 0) {
      // Select custom discount (id -1)
      onDiscountSelect(TX_PLAN_DISCOUNT_CUSTOM_ID, tabKey)
      setShowCustomDiscountInput(false)

      // Immediately close all submenu popups
      const allSubmenuPopups = document.querySelectorAll('.e-menu-popup')
      allSubmenuPopups.forEach((popup) => {
        const popupElement = popup as HTMLElement
        if (isPlanActionSubmenuPopup(popupElement)) {
          popupElement.remove()
        }
      })

      // Close the submenu and main menu
      setTimeout(() => {
        setMenuIsOpen(false)
      }, 50)
    } else {
      // No valid value, just hide input after a delay
      setTimeout(() => {
        setShowCustomDiscountInput(false)
      }, 200)
    }
  }, [customDiscountValue, onDiscountSelect, tabKey])

  // Handle custom discount input key press
  const handleCustomDiscountInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement> | KeyboardEvent): void => {
      if (e.key === 'Enter') {
        e.preventDefault()
        // If a value was entered, select custom discount and close menu
        if (customDiscountValue !== '' && Number(customDiscountValue) > 0) {
          // Select custom discount (id -1)
          onDiscountSelect(TX_PLAN_DISCOUNT_CUSTOM_ID, tabKey)
          setShowCustomDiscountInput(false)

          // Immediately close all submenu popups
          const allSubmenuPopups = document.querySelectorAll('.e-menu-popup')
          allSubmenuPopups.forEach((popup) => {
            const popupElement = popup as HTMLElement
            if (isPlanActionSubmenuPopup(popupElement)) {
              popupElement.remove()
            }
          })

          // Close the submenu and main menu
          setTimeout(() => {
            setMenuIsOpen(false)
          }, 50)
        } else {
          // No valid value, just hide input
          setShowCustomDiscountInput(false)
        }
      } else if (e.key === 'Escape') {
        setShowCustomDiscountInput(false)
        setCustomDiscountValue(customDiscount.toString())

        // Immediately close all submenu popups
        const allSubmenuPopups = document.querySelectorAll('.e-menu-popup')
        allSubmenuPopups.forEach((popup) => {
          const popupElement = popup as HTMLElement
          if (isPlanActionSubmenuPopup(popupElement)) {
            popupElement.remove()
          }
        })

        // Close the submenu and main menu
        setTimeout(() => {
          setMenuIsOpen(false)
        }, 50)
      }
    },
    [customDiscount, customDiscountValue, onDiscountSelect, tabKey],
  )

  // Helper function to get viewport dimensions
  const getViewportDimensions = useCallback((): {
    width: number
    height: number
  } => {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    }
  }, [])

  // Update menu position based on button location with viewport boundary checks
  const updateMenuPosition = useCallback((): void => {
    if (!containerRef.current) {
      return
    }
    const rect = containerRef.current.getBoundingClientRect()
    const viewport = getViewportDimensions()
    const margin = 10
    let menuWidth = DEFAULT_MENU_WIDTH
    let menuHeight = DEFAULT_MENU_HEIGHT
    const portalEl = portalRef.current
    if (portalEl) {
      const portalRect = portalEl.getBoundingClientRect()
      if (portalRect.width > 0) {
        menuWidth = portalRect.width
      }
      if (portalRect.height > 0) {
        menuHeight = portalRect.height
      }
    } else if (menuRef.current?.element) {
      const menuRect = menuRef.current.element.getBoundingClientRect()
      if (menuRect.width > 0) {
        menuWidth = menuRect.width
      }
      if (menuRect.height > 0) {
        menuHeight = menuRect.height
      }
    }
    // Right-align menu to the trigger button
    let left = rect.right - menuWidth
    let top = rect.bottom + MENU_GAP
    if (left + menuWidth > viewport.width - margin) {
      left = viewport.width - menuWidth - margin
    }
    if (left < margin) {
      left = margin
    }
    if (top + menuHeight > viewport.height - margin) {
      top = rect.top - menuHeight - MENU_GAP
      if (top < margin) {
        top = margin
      }
    }
    if (top < margin) {
      top = margin
    }
    setMenuPosition({
      top,
      left,
    })
  }, [getViewportDimensions])

  // Toggle menu visibility
  const toggleMenu = (): void => {
    const willBeOpen = !menuIsOpen
    // Calculate position before toggling state to ensure it's set on first click
    if (containerRef.current && willBeOpen) {
      updateMenuPosition()
    }
    setMenuIsOpen(willBeOpen)
  }

  // Handle click on discount-custom item to prevent submenu from closing
  const handleCustomDiscountClick = useCallback(
    (event: MouseEvent): void => {
      const target = event.target as HTMLElement
      const customItem = target.closest('#discount-custom') as HTMLElement
      if (customItem) {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()

        // Show custom discount input
        setShowCustomDiscountInput(true)
        const currentCustomValue =
          selectedDiscountId === TX_PLAN_DISCOUNT_CUSTOM_ID
            ? customDiscount.toString()
            : '0'
        setCustomDiscountValue(currentCustomValue)

        // Keep submenu open - find the discount menu item
        const menuElement = menuRef.current?.element
        if (menuElement) {
          const discountMenuItem = menuElement.querySelector(
            '#select-discount',
          ) as HTMLElement
          if (discountMenuItem) {
            discountMenuItem.setAttribute('aria-expanded', 'true')
            discountMenuItem.classList.add('e-focused')
          }
        }
      }
    },
    [selectedDiscountId, customDiscount],
  )

  // Reposition submenu to the left and prevent auto-closing
  useEffect(() => {
    if (!menuIsOpen || !menuRef.current) return

    const menuElement = menuRef.current?.element
    if (!menuElement) return

    let duplicateSubmenuItem: HTMLElement | null = null
    let duplicateSubmenuPopup: HTMLElement | null = null
    let insuranceSubmenuItem: HTMLElement | null = null
    let insuranceSubmenuPopup: HTMLElement | null = null
    let discountSubmenuItem: HTMLElement | null = null
    let discountSubmenuPopup: HTMLElement | null = null
    let providersSubmenuItem: HTMLElement | null = null
    let providersSubmenuPopup: HTMLElement | null = null
    let isMouseOverMenuItem = false
    let isMouseOverSubmenu = false
    let closeTimeout: NodeJS.Timeout | null = null
    let customInputInjectionTimeout: NodeJS.Timeout | null = null
    let customInputInjectionAttempts = 0
    let isInjectingCustomInput = false
    const CLOSE_DELAY = 200 // Short delay (200ms) before closing submenu to handle gaps and shaky mouse movement
    const MAX_INJECTION_ATTEMPTS = 5 // Maximum attempts to inject custom input

    // Keep submenu open when mouse is present
    const submenuElements = (
      submenuType: PlanActionSubmenuKind,
    ): { item: HTMLElement | null; popup: HTMLElement | null } => {
      if (submenuType === 'duplicate') {
        return { item: duplicateSubmenuItem, popup: duplicateSubmenuPopup }
      }
      if (submenuType === 'insurance') {
        return { item: insuranceSubmenuItem, popup: insuranceSubmenuPopup }
      }
      if (submenuType === 'providers') {
        return { item: providersSubmenuItem, popup: providersSubmenuPopup }
      }
      return { item: discountSubmenuItem, popup: discountSubmenuPopup }
    }

    const keepSubmenuOpen = (submenuType: PlanActionSubmenuKind): void => {
      const { item: submenuItem, popup: submenuPopup } =
        submenuElements(submenuType)

      if (submenuItem && submenuPopup) {
        // Clear any pending close timeout
        if (closeTimeout) {
          clearTimeout(closeTimeout)
          closeTimeout = null
        }
        submenuItem.setAttribute('aria-expanded', 'true')
        submenuItem.classList.add('e-focused')
      }
    }

    // Clear focused state and close submenu
    const clearSubmenuState = (submenuType?: PlanActionSubmenuKind): void => {
      // Don't clear if custom discount input is showing
      if (showCustomDiscountInput && submenuType === 'discount') {
        return
      }

      if (closeTimeout) {
        clearTimeout(closeTimeout)
        closeTimeout = null
      }

      if (submenuType === 'duplicate') {
        if (duplicateSubmenuItem) {
          duplicateSubmenuItem.setAttribute('aria-expanded', 'false')
          duplicateSubmenuItem.classList.remove('e-focused')
          duplicateSubmenuItem.blur()
        }
        if (duplicateSubmenuPopup) {
          duplicateSubmenuPopup.removeAttribute('data-positioned')
          duplicateSubmenuPopup.style.visibility = ''
          duplicateSubmenuPopup.style.opacity = ''
        }
        duplicateSubmenuPopup = null
        duplicateSubmenuItem = null
      } else if (submenuType === 'insurance') {
        if (insuranceSubmenuItem) {
          insuranceSubmenuItem.setAttribute('aria-expanded', 'false')
          insuranceSubmenuItem.classList.remove('e-focused')
          insuranceSubmenuItem.blur()
        }
        if (insuranceSubmenuPopup) {
          insuranceSubmenuPopup.removeAttribute('data-positioned')
          insuranceSubmenuPopup.style.visibility = ''
          insuranceSubmenuPopup.style.opacity = ''
        }
        insuranceSubmenuPopup = null
        insuranceSubmenuItem = null
      } else if (submenuType === 'discount') {
        if (discountSubmenuItem) {
          discountSubmenuItem.setAttribute('aria-expanded', 'false')
          discountSubmenuItem.classList.remove('e-focused')
          discountSubmenuItem.blur()
        }
        if (discountSubmenuPopup) {
          discountSubmenuPopup.removeAttribute('data-positioned')
          discountSubmenuPopup.style.visibility = ''
          discountSubmenuPopup.style.opacity = ''
        }
        discountSubmenuPopup = null
        discountSubmenuItem = null
      } else if (submenuType === 'providers') {
        if (providersSubmenuItem) {
          providersSubmenuItem.setAttribute('aria-expanded', 'false')
          providersSubmenuItem.classList.remove('e-focused')
          providersSubmenuItem.blur()
        }
        if (providersSubmenuPopup) {
          providersSubmenuPopup.removeAttribute('data-positioned')
          providersSubmenuPopup.style.visibility = ''
          providersSubmenuPopup.style.opacity = ''
        }
        providersSubmenuPopup = null
        providersSubmenuItem = null
      } else {
        // Clear all if no type specified
        if (duplicateSubmenuItem) {
          duplicateSubmenuItem.setAttribute('aria-expanded', 'false')
          duplicateSubmenuItem.classList.remove('e-focused')
          duplicateSubmenuItem.blur()
        }
        if (insuranceSubmenuItem) {
          insuranceSubmenuItem.setAttribute('aria-expanded', 'false')
          insuranceSubmenuItem.classList.remove('e-focused')
          insuranceSubmenuItem.blur()
        }
        if (discountSubmenuItem) {
          discountSubmenuItem.setAttribute('aria-expanded', 'false')
          discountSubmenuItem.classList.remove('e-focused')
          discountSubmenuItem.blur()
        }
        if (providersSubmenuItem) {
          providersSubmenuItem.setAttribute('aria-expanded', 'false')
          providersSubmenuItem.classList.remove('e-focused')
          providersSubmenuItem.blur()
        }
        if (duplicateSubmenuPopup) {
          duplicateSubmenuPopup.removeAttribute('data-positioned')
          duplicateSubmenuPopup.style.visibility = ''
          duplicateSubmenuPopup.style.opacity = ''
        }
        if (insuranceSubmenuPopup) {
          insuranceSubmenuPopup.removeAttribute('data-positioned')
          insuranceSubmenuPopup.style.visibility = ''
          insuranceSubmenuPopup.style.opacity = ''
        }
        if (discountSubmenuPopup) {
          discountSubmenuPopup.removeAttribute('data-positioned')
          discountSubmenuPopup.style.visibility = ''
          discountSubmenuPopup.style.opacity = ''
        }
        if (providersSubmenuPopup) {
          providersSubmenuPopup.removeAttribute('data-positioned')
          providersSubmenuPopup.style.visibility = ''
          providersSubmenuPopup.style.opacity = ''
        }
        duplicateSubmenuPopup = null
        duplicateSubmenuItem = null
        insuranceSubmenuPopup = null
        insuranceSubmenuItem = null
        discountSubmenuPopup = null
        discountSubmenuItem = null
        providersSubmenuPopup = null
        providersSubmenuItem = null
      }

      isMouseOverMenuItem = false
      isMouseOverSubmenu = false
    }

    // Schedule submenu to close after short delay
    // If mouse re-enters parent or submenu before delay fires, cancel the close
    const scheduleSubmenuClose = (submenuType: PlanActionSubmenuKind): void => {
      // Don't close if custom discount input is showing
      if (showCustomDiscountInput && submenuType === 'discount') {
        return
      }

      // Clear any existing timeout
      if (closeTimeout) {
        clearTimeout(closeTimeout)
        closeTimeout = null
      }

      // Schedule close after short delay
      closeTimeout = setTimeout(() => {
        // Don't close if custom discount input is showing
        if (showCustomDiscountInput && submenuType === 'discount') {
          return
        }
        // Double-check that mouse hasn't re-entered parent or submenu
        const currentSubmenuPopup = submenuElements(submenuType).popup
        if (
          currentSubmenuPopup &&
          document.body.contains(currentSubmenuPopup)
        ) {
          // Only close if mouse is still not over menu item or submenu
          if (!isMouseOverMenuItem && !isMouseOverSubmenu) {
            clearSubmenuState(submenuType)
          }
        } else if (!currentSubmenuPopup) {
          // Submenu was already removed, just clear state
          if (submenuType === 'duplicate') {
            duplicateSubmenuItem = null
          } else if (submenuType === 'insurance') {
            insuranceSubmenuItem = null
          } else if (submenuType === 'providers') {
            providersSubmenuItem = null
          } else {
            discountSubmenuItem = null
          }
          isMouseOverMenuItem = false
          isMouseOverSubmenu = false
        }
        closeTimeout = null
      }, CLOSE_DELAY)
    }

    const repositionSubmenu = (): void => {
      // Find all submenu popups
      const allPopups = document.querySelectorAll('.e-menu-popup')
      const menuRect = menuElement.getBoundingClientRect()
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      }
      const margin = 10 // Minimum margin from viewport edges

      // Reset submenu tracking
      duplicateSubmenuPopup = null
      insuranceSubmenuPopup = null
      discountSubmenuPopup = null
      providersSubmenuPopup = null

      // Helper function to calculate optimal submenu position
      const calculateSubmenuPosition = (
        itemRect: DOMRect,
        popupRect: DOMRect,
      ): { left: number; top: number } => {
        const availableLeft = itemRect.left
        const availableRight = viewport.width - itemRect.right
        const submenuWidth = popupRect.width || 200 // Fallback width if not yet rendered
        const submenuHeight = popupRect.height || 300 // Fallback height if not yet rendered

        // Determine horizontal position based on available space
        let newLeft: number
        if (availableLeft >= submenuWidth + margin) {
          // Enough space on left, position to the left
          newLeft = itemRect.left - submenuWidth - 2
        } else if (availableRight >= submenuWidth + margin) {
          // Not enough space on left, but enough on right, position to the right
          newLeft = itemRect.right + 2
        } else {
          // Not enough space on either side, prefer left but adjust to fit
          if (availableLeft > availableRight) {
            // More space on left, position to left but adjust
            newLeft = Math.max(margin, itemRect.left - submenuWidth - 2)
          } else {
            // More space on right, position to right but adjust
            newLeft = Math.min(
              viewport.width - submenuWidth - margin,
              itemRect.right + 2,
            )
          }
        }

        // Ensure submenu doesn't overflow left edge
        if (newLeft < margin) {
          newLeft = margin
        }
        // Ensure submenu doesn't overflow right edge
        if (newLeft + submenuWidth > viewport.width - margin) {
          newLeft = viewport.width - submenuWidth - margin
        }

        // Calculate vertical position
        let newTop = itemRect.top

        // Ensure submenu doesn't overflow bottom edge
        if (newTop + submenuHeight > viewport.height - margin) {
          newTop = viewport.height - submenuHeight - margin
          // If submenu is too tall, align to top of viewport
          if (newTop < margin) {
            newTop = margin
          }
        }

        // Ensure submenu doesn't overflow top edge
        if (newTop < margin) {
          newTop = margin
        }

        return { left: newLeft, top: newTop }
      }

      allPopups.forEach((popup) => {
        const popupElement = popup as HTMLElement
        const popupRect = popupElement.getBoundingClientRect()

        // Check if this popup contains duplicate, discount or insurance items
        const hasDuplicateItems =
          popupElement.querySelector('[id^="duplicate-plan-"]') !== null
        const hasDiscountItems =
          popupElement.querySelector('#discount-custom') !== null ||
          popupElement.querySelector('[id^="discount-"]') !== null
        const hasInsuranceItems =
          popupElement.querySelector('[id^="insurance-"]') !== null
        const hasProviderItems =
          popupElement.querySelector('[id^="provider-menu-"]') !== null

        // Only process popups that are related to our menu
        if (
          (popupRect.left > menuRect.left &&
            Math.abs(popupRect.top - menuRect.top) < 200) ||
          hasDuplicateItems ||
          hasDiscountItems ||
          hasInsuranceItems ||
          hasProviderItems
        ) {
          // Function to position the submenu
          const positionSubmenu = (): void => {
            // Get fresh dimensions
            const currentPopupRect = popupElement.getBoundingClientRect()

            // Find the parent menu item that triggered this submenu
            const menuItemElements =
              menuElement.querySelectorAll('.e-menu-item')
            menuItemElements.forEach((item) => {
              const itemElement = item as HTMLElement
              const isDuplicateItem = itemElement.id === 'select-duplicate'
              const isInsuranceItem = itemElement.id === 'select-insurance'
              const isDiscountItem = itemElement.id === 'select-discount'
              const isProvidersItem = itemElement.id === 'select-providers'
              const isExpanded =
                itemElement.classList.contains('e-focused') ||
                itemElement.getAttribute('aria-expanded') === 'true'

              // Match duplicate submenu with duplicate parent
              if (hasDuplicateItems && isDuplicateItem && isExpanded) {
                duplicateSubmenuItem = itemElement
                duplicateSubmenuPopup = popupElement
                const itemRect = itemElement.getBoundingClientRect()
                const position = calculateSubmenuPosition(
                  itemRect,
                  currentPopupRect,
                )
                popupElement.style.position = 'fixed'
                popupElement.style.left = `${position.left}px`
                popupElement.style.top = `${position.top}px`
                popupElement.style.right = 'auto'
                popupElement.style.visibility = 'visible'
                popupElement.style.opacity = '1'
                popupElement.setAttribute('data-positioned', 'true')
              }
              // Match insurance submenu with insurance parent
              else if (hasInsuranceItems && isInsuranceItem && isExpanded) {
                insuranceSubmenuItem = itemElement
                insuranceSubmenuPopup = popupElement
                const itemRect = itemElement.getBoundingClientRect()
                const position = calculateSubmenuPosition(
                  itemRect,
                  currentPopupRect,
                )
                popupElement.style.position = 'fixed'
                popupElement.style.left = `${position.left}px`
                popupElement.style.top = `${position.top}px`
                popupElement.style.right = 'auto'
                popupElement.style.visibility = 'visible'
                popupElement.style.opacity = '1'
                popupElement.setAttribute('data-positioned', 'true')
              }
              // Match discount submenu with discount parent
              else if (hasDiscountItems && isDiscountItem && isExpanded) {
                discountSubmenuItem = itemElement
                discountSubmenuPopup = popupElement
                const itemRect = itemElement.getBoundingClientRect()
                const position = calculateSubmenuPosition(
                  itemRect,
                  currentPopupRect,
                )
                popupElement.style.position = 'fixed'
                popupElement.style.left = `${position.left}px`
                popupElement.style.top = `${position.top}px`
                popupElement.style.right = 'auto'
                popupElement.style.visibility = 'visible'
                popupElement.style.opacity = '1'
                popupElement.setAttribute('data-positioned', 'true')
              } else if (hasProviderItems && isProvidersItem && isExpanded) {
                providersSubmenuItem = itemElement
                providersSubmenuPopup = popupElement
                const itemRect = itemElement.getBoundingClientRect()
                const position = calculateSubmenuPosition(
                  itemRect,
                  currentPopupRect,
                )
                popupElement.style.position = 'fixed'
                popupElement.style.left = `${position.left}px`
                popupElement.style.top = `${position.top}px`
                popupElement.style.right = 'auto'
                popupElement.style.visibility = 'visible'
                popupElement.style.opacity = '1'
                popupElement.setAttribute('data-positioned', 'true')
              }
            })
          }

          // Check if submenu has proper dimensions (not still expanding)
          // If height is suspiciously large (likely initial render), wait for proper sizing
          const isInitialRender =
            (popupRect.height > viewport.height * 0.8 ||
              popupRect.width === 0 ||
              popupRect.height === 0) &&
            !popupElement.hasAttribute('data-positioned')

          // If it's an initial render, hide it temporarily and recalculate
          if (isInitialRender) {
            popupElement.style.visibility = 'hidden'
            popupElement.style.opacity = '0'
            // Wait for next frame to get proper dimensions
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                const updatedRect = popupElement.getBoundingClientRect()
                // Only proceed if dimensions are now reasonable
                if (
                  updatedRect.height < viewport.height * 0.8 &&
                  updatedRect.width > 0 &&
                  updatedRect.height > 0
                ) {
                  positionSubmenu()
                } else {
                  // If still not ready, show it anyway after a short delay
                  setTimeout(() => {
                    positionSubmenu()
                  }, 50)
                }
              })
            })
          } else {
            // Position submenu immediately if dimensions are reasonable
            positionSubmenu()
          }
        }
      })
    }

    // Handle mouseenter on menu item
    // Cancel close timer if mouse re-enters parent menu item
    const handleMenuItemEnter = (event: MouseEvent): void => {
      const target = event.target as HTMLElement
      const menuItem = target.closest('.e-menu-item') as HTMLElement

      if (menuItem && menuItem.id === 'select-duplicate') {
        isMouseOverMenuItem = true
        // Cancel any pending close timer
        if (closeTimeout) {
          clearTimeout(closeTimeout)
          closeTimeout = null
        }
        keepSubmenuOpen('duplicate')
      } else if (menuItem && menuItem.id === 'select-insurance') {
        isMouseOverMenuItem = true
        // Cancel any pending close timer
        if (closeTimeout) {
          clearTimeout(closeTimeout)
          closeTimeout = null
        }
        keepSubmenuOpen('insurance')
      } else if (menuItem && menuItem.id === 'select-discount') {
        isMouseOverMenuItem = true
        // Cancel any pending close timer
        if (closeTimeout) {
          clearTimeout(closeTimeout)
          closeTimeout = null
        }
        keepSubmenuOpen('discount')
      } else if (menuItem && menuItem.id === 'select-providers') {
        isMouseOverMenuItem = true
        if (closeTimeout) {
          clearTimeout(closeTimeout)
          closeTimeout = null
        }
        keepSubmenuOpen('providers')
      }
    }

    // Handle mouseleave on menu item
    // Start close timer - if mouse re-enters parent or submenu, timer will be cancelled
    const handleMenuItemLeave = (event: MouseEvent): void => {
      const target = event.target as HTMLElement
      const menuItem = target.closest('.e-menu-item') as HTMLElement

      if (menuItem && menuItem.id === 'select-duplicate') {
        isMouseOverMenuItem = false
        // Check if mouse is moving directly to submenu
        const relatedTarget = event.relatedTarget as HTMLElement
        const isMovingToSubmenu =
          relatedTarget &&
          (duplicateSubmenuPopup?.contains(relatedTarget) ||
            relatedTarget === duplicateSubmenuPopup)

        // If moving directly to submenu, keep menu item focused and don't schedule close
        if (isMovingToSubmenu) {
          menuItem.setAttribute('aria-expanded', 'true')
          menuItem.classList.add('e-focused')
        } else {
          // Otherwise, start close timer (will be cancelled if mouse re-enters)
          scheduleSubmenuClose('duplicate')
        }
      } else if (menuItem && menuItem.id === 'select-insurance') {
        isMouseOverMenuItem = false
        // Check if mouse is moving directly to submenu
        const relatedTarget = event.relatedTarget as HTMLElement
        const isMovingToSubmenu =
          relatedTarget &&
          (insuranceSubmenuPopup?.contains(relatedTarget) ||
            relatedTarget === insuranceSubmenuPopup)

        // If moving directly to submenu, keep menu item focused and don't schedule close
        if (isMovingToSubmenu) {
          menuItem.setAttribute('aria-expanded', 'true')
          menuItem.classList.add('e-focused')
        } else {
          // Otherwise, start close timer (will be cancelled if mouse re-enters)
          scheduleSubmenuClose('insurance')
        }
      } else if (menuItem && menuItem.id === 'select-discount') {
        isMouseOverMenuItem = false
        // Check if mouse is moving directly to submenu
        const relatedTarget = event.relatedTarget as HTMLElement
        const isMovingToSubmenu =
          relatedTarget &&
          (discountSubmenuPopup?.contains(relatedTarget) ||
            relatedTarget === discountSubmenuPopup)

        // If moving directly to submenu, keep menu item focused and don't schedule close
        if (isMovingToSubmenu) {
          menuItem.setAttribute('aria-expanded', 'true')
          menuItem.classList.add('e-focused')
        } else {
          // Otherwise, start close timer (will be cancelled if mouse re-enters)
          scheduleSubmenuClose('discount')
        }
      } else if (menuItem && menuItem.id === 'select-providers') {
        isMouseOverMenuItem = false
        const relatedTarget = event.relatedTarget as HTMLElement
        const isMovingToSubmenu =
          relatedTarget &&
          (providersSubmenuPopup?.contains(relatedTarget) ||
            relatedTarget === providersSubmenuPopup)
        if (isMovingToSubmenu) {
          menuItem.setAttribute('aria-expanded', 'true')
          menuItem.classList.add('e-focused')
        } else {
          scheduleSubmenuClose('providers')
        }
      }
    }

    // Handle mouseenter on submenu popup
    // Cancel close timer if mouse enters submenu
    const handleSubmenuEnter = (event: MouseEvent): void => {
      const target = event.target as HTMLElement
      // Check which submenu this belongs to
      if (
        duplicateSubmenuPopup &&
        duplicateSubmenuPopup.contains(target as Node)
      ) {
        isMouseOverSubmenu = true
        // Cancel any pending close timer
        if (closeTimeout) {
          clearTimeout(closeTimeout)
          closeTimeout = null
        }
        keepSubmenuOpen('duplicate')
      } else if (
        insuranceSubmenuPopup &&
        insuranceSubmenuPopup.contains(target as Node)
      ) {
        isMouseOverSubmenu = true
        // Cancel any pending close timer
        if (closeTimeout) {
          clearTimeout(closeTimeout)
          closeTimeout = null
        }
        keepSubmenuOpen('insurance')
      } else if (
        discountSubmenuPopup &&
        discountSubmenuPopup.contains(target as Node)
      ) {
        isMouseOverSubmenu = true
        // Cancel any pending close timer
        if (closeTimeout) {
          clearTimeout(closeTimeout)
          closeTimeout = null
        }
        keepSubmenuOpen('discount')
      } else if (
        providersSubmenuPopup &&
        providersSubmenuPopup.contains(target as Node)
      ) {
        isMouseOverSubmenu = true
        if (closeTimeout) {
          clearTimeout(closeTimeout)
          closeTimeout = null
        }
        keepSubmenuOpen('providers')
      }
    }

    // Handle mouseleave on submenu popup
    // Start close timer - if mouse re-enters parent or submenu, timer will be cancelled
    const handleSubmenuLeave = (event: MouseEvent): void => {
      const target = event.target as HTMLElement
      // Check which submenu this belongs to
      if (
        duplicateSubmenuPopup &&
        duplicateSubmenuPopup.contains(target as Node)
      ) {
        isMouseOverSubmenu = false
        // Check if mouse is moving directly to menu item
        const relatedTarget = event.relatedTarget as HTMLElement
        const isMovingToMenuItem =
          relatedTarget &&
          (duplicateSubmenuItem?.contains(relatedTarget) ||
            relatedTarget === duplicateSubmenuItem)

        // If moving directly to menu item, don't schedule close
        if (!isMovingToMenuItem) {
          scheduleSubmenuClose('duplicate')
        }
      } else if (
        insuranceSubmenuPopup &&
        insuranceSubmenuPopup.contains(target as Node)
      ) {
        isMouseOverSubmenu = false
        // Check if mouse is moving directly to menu item
        const relatedTarget = event.relatedTarget as HTMLElement
        const isMovingToMenuItem =
          relatedTarget &&
          (insuranceSubmenuItem?.contains(relatedTarget) ||
            relatedTarget === insuranceSubmenuItem)

        // If moving directly to menu item, don't schedule close
        if (!isMovingToMenuItem) {
          scheduleSubmenuClose('insurance')
        }
      } else if (
        discountSubmenuPopup &&
        discountSubmenuPopup.contains(target as Node)
      ) {
        isMouseOverSubmenu = false
        // Check if mouse is moving directly to menu item
        const relatedTarget = event.relatedTarget as HTMLElement
        const isMovingToMenuItem =
          relatedTarget &&
          (discountSubmenuItem?.contains(relatedTarget) ||
            relatedTarget === discountSubmenuItem)

        // If moving directly to menu item, don't schedule close
        if (!isMovingToMenuItem) {
          scheduleSubmenuClose('discount')
        }
      } else if (
        providersSubmenuPopup &&
        providersSubmenuPopup.contains(target as Node)
      ) {
        isMouseOverSubmenu = false
        const relatedTarget = event.relatedTarget as HTMLElement
        const isMovingToMenuItem =
          relatedTarget &&
          (providersSubmenuItem?.contains(relatedTarget) ||
            relatedTarget === providersSubmenuItem)
        if (!isMovingToMenuItem) {
          scheduleSubmenuClose('providers')
        }
      }
    }

    // Handle mouseenter on other menu items to close submenu
    const handleOtherItemHover = (event: MouseEvent): void => {
      const target = event.target as HTMLElement

      // Only check if we're inside the main menu element
      if (!menuElement.contains(target)) {
        return
      }

      const menuItem = target.closest('.e-menu-item') as HTMLElement

      // If hovering over a different menu item in the main menu (not the one with submenu), close submenu
      if (
        menuItem &&
        menuItem.id !== 'select-duplicate' &&
        menuItem.id !== 'select-insurance' &&
        menuItem.id !== 'select-discount' &&
        menuItem.id !== 'select-providers' &&
        menuElement.contains(menuItem) // Make sure it's in our main menu
      ) {
        // Close duplicate submenu if it's open and we're not hovering over duplicate
        if (
          duplicateSubmenuItem &&
          menuItem !== duplicateSubmenuItem &&
          duplicateSubmenuPopup
        ) {
          clearSubmenuState('duplicate')
        }
        // Close insurance submenu if it's open and we're not hovering over insurance
        if (
          insuranceSubmenuItem &&
          menuItem !== insuranceSubmenuItem &&
          insuranceSubmenuPopup
        ) {
          clearSubmenuState('insurance')
        }
        // Close discount submenu if it's open and we're not hovering over discount
        if (
          discountSubmenuItem &&
          menuItem !== discountSubmenuItem &&
          discountSubmenuPopup
        ) {
          clearSubmenuState('discount')
        }
        if (
          providersSubmenuItem &&
          menuItem !== providersSubmenuItem &&
          providersSubmenuPopup
        ) {
          clearSubmenuState('providers')
        }
      }
    }

    // Inject custom discount input into discount submenu
    const injectCustomDiscountInput = (): void => {
      // Prevent concurrent injections
      if (isInjectingCustomInput) {
        return
      }

      if (!showCustomDiscountInput) {
        // Remove input if it exists and we're hiding it
        const existingInput = document.querySelector(
          '.custom-discount-input-wrapper',
        )
        if (existingInput) {
          existingInput.remove()
        }
        // Reset injection attempts
        customInputInjectionAttempts = 0
        if (customInputInjectionTimeout) {
          clearTimeout(customInputInjectionTimeout)
          customInputInjectionTimeout = null
        }
        isInjectingCustomInput = false
        return
      }

      // Prevent infinite loops - check if we've exceeded max attempts
      if (customInputInjectionAttempts >= MAX_INJECTION_ATTEMPTS) {
        customInputInjectionAttempts = 0
        isInjectingCustomInput = false
        return
      }

      // Check if input already exists globally to avoid duplicate injections
      const existingGlobalInput = document.querySelector(
        '.custom-discount-input-wrapper',
      )
      if (existingGlobalInput) {
        // Input already exists, no need to inject again
        isInjectingCustomInput = false
        return
      }

      // Mark as injecting
      isInjectingCustomInput = true

      // Find all submenu popups and look for the discount one
      const allPopups = document.querySelectorAll('.e-menu-popup')
      let discountPopup: HTMLElement | null = null

      for (let i = 0; i < allPopups.length; i++) {
        const popupElement = allPopups[i] as HTMLElement
        const hasDiscountCustom = popupElement.querySelector('#discount-custom')
        if (hasDiscountCustom) {
          discountPopup = popupElement
          break
        }
      }

      if (!discountPopup) {
        // If popup not found, try again after a short delay (might be hidden)
        customInputInjectionAttempts++
        if (customInputInjectionTimeout) {
          clearTimeout(customInputInjectionTimeout)
        }
        customInputInjectionTimeout = setTimeout(() => {
          isInjectingCustomInput = false
          injectCustomDiscountInput()
        }, 100)
        return
      }

      // Reset attempts on success
      customInputInjectionAttempts = 0

      const customMenuItem = discountPopup.querySelector(
        '#discount-custom',
      ) as HTMLElement | null
      if (!customMenuItem) return

      // Check if input already exists in the parent (ul) element
      const parentElement = customMenuItem.parentElement
      if (!parentElement) return

      // Remove existing input if it exists (to avoid duplicates)
      const existingParentInput = parentElement.querySelector(
        '.custom-discount-input-wrapper',
      )
      if (existingParentInput) {
        existingParentInput.remove()
      }

      // Create input wrapper
      const inputWrapper = document.createElement('div')
      inputWrapper.className = 'custom-discount-input-wrapper'
      inputWrapper.style.cssText =
        'padding: 8px 12px; border-top: 1px solid var(--gray-200); background-color: var(--gray-white);'

      const inputContainer = document.createElement('div')
      inputContainer.style.cssText =
        'display: flex; align-items: center; gap: 4px;'

      const input = document.createElement('input')
      input.type = 'number'
      input.value = customDiscountValue
      input.placeholder = 'Enter %'
      input.className = 'custom-discount-input'
      input.style.cssText =
        'flex: 1; padding: 4px 8px; border: 1px solid var(--gray-300); border-radius: 4px; font-size: 14px; outline: none;'
      input.addEventListener('input', handleCustomDiscountInputChange)
      input.addEventListener('blur', handleCustomDiscountInputBlur)
      input.addEventListener('keydown', handleCustomDiscountInputKeyDown)

      const percentLabel = document.createElement('span')
      percentLabel.textContent = '%'
      percentLabel.style.cssText = 'font-size: 14px; color: var(--gray-600);'

      inputContainer.appendChild(input)
      inputContainer.appendChild(percentLabel)
      inputWrapper.appendChild(inputContainer)

      // Insert after the custom menu item in the parent list
      parentElement.insertBefore(inputWrapper, customMenuItem.nextSibling)

      // Ensure submenu is visible when showing input
      if (discountPopup) {
        discountPopup.style.visibility = 'visible'
        discountPopup.style.opacity = '1'
      }

      // Keep submenu open
      if (discountSubmenuItem && discountSubmenuItem.id === 'select-discount') {
        discountSubmenuItem.setAttribute('aria-expanded', 'true')
        discountSubmenuItem.classList.add('e-focused')
      }

      // Focus input after a short delay to ensure it's rendered
      setTimeout(() => {
        const inputElement = inputWrapper.querySelector(
          '.custom-discount-input',
        ) as HTMLInputElement
        if (inputElement) {
          inputElement.focus()
          inputElement.select()
        }
        // Mark injection as complete
        isInjectingCustomInput = false
      }, 200)
    }

    // Use MutationObserver to watch for submenu popups
    const observer = new MutationObserver(() => {
      repositionSubmenu()

      // Re-attach event listeners to duplicate submenu popup when it appears
      if (
        duplicateSubmenuPopup &&
        !duplicateSubmenuPopup.hasAttribute('data-listeners-attached')
      ) {
        duplicateSubmenuPopup.setAttribute('data-listeners-attached', 'true')
        duplicateSubmenuPopup.addEventListener('mouseenter', handleSubmenuEnter)
        duplicateSubmenuPopup.addEventListener('mouseleave', handleSubmenuLeave)
      }

      // Re-attach event listeners to insurance submenu popup when it appears
      if (
        insuranceSubmenuPopup &&
        !insuranceSubmenuPopup.hasAttribute('data-listeners-attached')
      ) {
        insuranceSubmenuPopup.setAttribute('data-listeners-attached', 'true')
        insuranceSubmenuPopup.addEventListener('mouseenter', handleSubmenuEnter)
        insuranceSubmenuPopup.addEventListener('mouseleave', handleSubmenuLeave)
      }

      // Re-attach event listeners to discount submenu popup when it appears
      if (
        discountSubmenuPopup &&
        !discountSubmenuPopup.hasAttribute('data-listeners-attached')
      ) {
        discountSubmenuPopup.setAttribute('data-listeners-attached', 'true')
        discountSubmenuPopup.addEventListener('mouseenter', handleSubmenuEnter)
        discountSubmenuPopup.addEventListener('mouseleave', handleSubmenuLeave)
      }

      if (
        providersSubmenuPopup &&
        !providersSubmenuPopup.hasAttribute('data-listeners-attached')
      ) {
        providersSubmenuPopup.setAttribute('data-listeners-attached', 'true')
        providersSubmenuPopup.addEventListener('mouseenter', handleSubmenuEnter)
        providersSubmenuPopup.addEventListener('mouseleave', handleSubmenuLeave)
      }

      // Attach click handler to discount-custom item
      const customDiscountItem = document.querySelector(
        '#discount-custom',
      ) as HTMLElement
      if (customDiscountItem) {
        // Remove existing listener if any
        customDiscountItem.removeEventListener(
          'click',
          handleCustomDiscountClick,
          true,
        )
        // Add new listener with capture phase to intercept before Syncfusion
        customDiscountItem.addEventListener(
          'click',
          handleCustomDiscountClick,
          true,
        )
      }

      // Inject custom discount input if needed
      injectCustomDiscountInput()
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-expanded', 'class'],
    })

    // Find and attach listeners to the menu items with submenus
    const findAndAttachMenuItemListeners = (): void => {
      const duplicateMenuItem = menuElement.querySelector(
        '#select-duplicate',
      ) as HTMLElement
      if (duplicateMenuItem) {
        duplicateMenuItem.addEventListener('mouseenter', handleMenuItemEnter)
        duplicateMenuItem.addEventListener('mouseleave', handleMenuItemLeave)
      }

      const insuranceMenuItem = menuElement.querySelector(
        '#select-insurance',
      ) as HTMLElement
      if (insuranceMenuItem) {
        insuranceMenuItem.addEventListener('mouseenter', handleMenuItemEnter)
        insuranceMenuItem.addEventListener('mouseleave', handleMenuItemLeave)
      }

      const discountMenuItem = menuElement.querySelector(
        '#select-discount',
      ) as HTMLElement
      if (discountMenuItem) {
        discountMenuItem.addEventListener('mouseenter', handleMenuItemEnter)
        discountMenuItem.addEventListener('mouseleave', handleMenuItemLeave)
      }

      const providersMenuItem = menuElement.querySelector(
        '#select-providers',
      ) as HTMLElement
      if (providersMenuItem) {
        providersMenuItem.addEventListener('mouseenter', handleMenuItemEnter)
        providersMenuItem.addEventListener('mouseleave', handleMenuItemLeave)
      }
    }

    // Initial setup
    repositionSubmenu()
    findAndAttachMenuItemListeners()

    // Periodically check for submenu and reposition (reduced frequency to prevent blinking)
    const interval = setInterval(() => {
      repositionSubmenu()
      findAndAttachMenuItemListeners()
      // Also try to inject custom discount input periodically (only if not already injected)
      if (showCustomDiscountInput) {
        const existingIntervalInput = document.querySelector(
          '.custom-discount-input-wrapper',
        )
        if (
          !existingIntervalInput &&
          customInputInjectionAttempts < MAX_INJECTION_ATTEMPTS
        ) {
          injectCustomDiscountInput()
        }
      }

      // Keep parent menu items focused if mouse is over submenu
      // Only update if the state has actually changed to avoid unnecessary DOM updates
      if (isMouseOverSubmenu) {
        if (
          duplicateSubmenuItem &&
          duplicateSubmenuPopup &&
          duplicateSubmenuItem.getAttribute('aria-expanded') !== 'true'
        ) {
          duplicateSubmenuItem.setAttribute('aria-expanded', 'true')
          duplicateSubmenuItem.classList.add('e-focused')
        }
        if (
          insuranceSubmenuItem &&
          insuranceSubmenuPopup &&
          insuranceSubmenuItem.getAttribute('aria-expanded') !== 'true'
        ) {
          insuranceSubmenuItem.setAttribute('aria-expanded', 'true')
          insuranceSubmenuItem.classList.add('e-focused')
        }
        if (
          discountSubmenuItem &&
          discountSubmenuPopup &&
          discountSubmenuItem.getAttribute('aria-expanded') !== 'true'
        ) {
          discountSubmenuItem.setAttribute('aria-expanded', 'true')
          discountSubmenuItem.classList.add('e-focused')
        }
        if (
          providersSubmenuItem &&
          providersSubmenuPopup &&
          providersSubmenuItem.getAttribute('aria-expanded') !== 'true'
        ) {
          providersSubmenuItem.setAttribute('aria-expanded', 'true')
          providersSubmenuItem.classList.add('e-focused')
        }
      }
    }, 200) // Reduced from 50ms to 200ms to prevent blinking

    // Add event listeners
    menuElement.addEventListener('mouseenter', handleOtherItemHover, true)

    return () => {
      observer.disconnect()
      clearInterval(interval)
      if (closeTimeout) {
        clearTimeout(closeTimeout)
      }
      if (customInputInjectionTimeout) {
        clearTimeout(customInputInjectionTimeout)
        customInputInjectionTimeout = null
      }
      customInputInjectionAttempts = 0
      isInjectingCustomInput = false
      menuElement.removeEventListener('mouseenter', handleOtherItemHover, true)

      // Remove menu item listeners
      const duplicateMenuItem = menuElement.querySelector(
        '#select-duplicate',
      ) as HTMLElement
      if (duplicateMenuItem) {
        duplicateMenuItem.removeEventListener('mouseenter', handleMenuItemEnter)
        duplicateMenuItem.removeEventListener('mouseleave', handleMenuItemLeave)
      }

      const insuranceMenuItem = menuElement.querySelector(
        '#select-insurance',
      ) as HTMLElement
      if (insuranceMenuItem) {
        insuranceMenuItem.removeEventListener('mouseenter', handleMenuItemEnter)
        insuranceMenuItem.removeEventListener('mouseleave', handleMenuItemLeave)
      }

      const discountMenuItem = menuElement.querySelector(
        '#select-discount',
      ) as HTMLElement
      if (discountMenuItem) {
        discountMenuItem.removeEventListener('mouseenter', handleMenuItemEnter)
        discountMenuItem.removeEventListener('mouseleave', handleMenuItemLeave)
      }

      const providersMenuItem = menuElement.querySelector(
        '#select-providers',
      ) as HTMLElement
      if (providersMenuItem) {
        providersMenuItem.removeEventListener('mouseenter', handleMenuItemEnter)
        providersMenuItem.removeEventListener('mouseleave', handleMenuItemLeave)
      }

      // Remove submenu popup listeners
      if (duplicateSubmenuPopup) {
        duplicateSubmenuPopup.removeEventListener(
          'mouseenter',
          handleSubmenuEnter,
        )
        duplicateSubmenuPopup.removeEventListener(
          'mouseleave',
          handleSubmenuLeave,
        )
      }
      if (insuranceSubmenuPopup) {
        insuranceSubmenuPopup.removeEventListener(
          'mouseenter',
          handleSubmenuEnter,
        )
        insuranceSubmenuPopup.removeEventListener(
          'mouseleave',
          handleSubmenuLeave,
        )
      }
      if (discountSubmenuPopup) {
        discountSubmenuPopup.removeEventListener(
          'mouseenter',
          handleSubmenuEnter,
        )
        discountSubmenuPopup.removeEventListener(
          'mouseleave',
          handleSubmenuLeave,
        )
      }
      if (providersSubmenuPopup) {
        providersSubmenuPopup.removeEventListener(
          'mouseenter',
          handleSubmenuEnter,
        )
        providersSubmenuPopup.removeEventListener(
          'mouseleave',
          handleSubmenuLeave,
        )
      }

      // Clear state on cleanup (but preserve if custom input is showing)
      if (!showCustomDiscountInput) {
        clearSubmenuState()
      }
    }
  }, [
    menuIsOpen,
    showCustomDiscountInput,
    customDiscountValue,
    selectedDiscountId,
    customDiscount,
    handleCustomDiscountInputBlur,
    handleCustomDiscountInputChange,
    handleCustomDiscountInputKeyDown,
    handleCustomDiscountClick,
  ])

  // Reset custom discount input and close all submenus when menu closes
  useEffect(() => {
    if (!menuIsOpen) {
      setShowCustomDiscountInput(false)

      // Close all submenu popups by removing them from DOM
      const allSubmenuPopups = document.querySelectorAll('.e-menu-popup')
      allSubmenuPopups.forEach((popup) => {
        const popupElement = popup as HTMLElement
        if (isPlanActionSubmenuPopup(popupElement)) {
          // Remove the submenu popup from DOM
          popupElement.remove()
        }
      })

      // Also close any submenu items in the main menu
      if (menuRef.current?.element) {
        const menuElement = menuRef.current.element
        const submenuItems = menuElement.querySelectorAll(
          '[aria-expanded="true"]',
        )
        submenuItems.forEach((item) => {
          const itemElement = item as HTMLElement
          itemElement.setAttribute('aria-expanded', 'false')
          itemElement.classList.remove('e-focused')
        })
      }
    }
  }, [menuIsOpen])

  // Update menu position immediately when menu opens
  useEffect(() => {
    if (menuIsOpen) {
      // Use requestAnimationFrame to ensure DOM is ready after state update
      requestAnimationFrame(() => {
        updateMenuPosition()
        // Refine position after menu renders to use actual height
        setTimeout(() => {
          updateMenuPosition()
        }, 50)
      })
    }
  }, [menuIsOpen, updateMenuPosition])

  useEffect(() => {
    if (!menuIsOpen || !onProviderMenuSelect) {
      return
    }
    const syncProviderMenuEnabled = (): void => {
      menuRef.current?.enableItems(
        ['provider-menu-assign'],
        !providerMenuDisabled.assignMissingDisabled,
        true,
      )
      menuRef.current?.enableItems(
        ['provider-menu-replace'],
        !providerMenuDisabled.replaceAllDisabled,
        true,
      )
      menuRef.current?.enableItems(
        ['provider-menu-clear'],
        !providerMenuDisabled.clearAllDisabled,
        true,
      )
    }
    requestAnimationFrame(() => {
      syncProviderMenuEnabled()
      setTimeout(syncProviderMenuEnabled, 50)
    })
  }, [menuIsOpen, onProviderMenuSelect, providerMenuDisabled])

  // Update menu position on scroll and resize
  useEffect(() => {
    if (!menuIsOpen) return

    const handleScroll = (): void => {
      updateMenuPosition()
    }

    const handleResize = (): void => {
      updateMenuPosition()
    }

    // Update position on scroll and resize
    // Use capture phase and also listen on document to catch scrolls on nested scrollable elements
    window.addEventListener('scroll', handleScroll, true)
    document.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
      document.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
    }
  }, [menuIsOpen, updateMenuPosition])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Element
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !target.closest('.e-menu-wrapper')
      ) {
        setMenuIsOpen(false)
      }
    }

    if (menuIsOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [menuIsOpen])

  return (
    <div ref={containerRef} className="plan-action-menu-container">
      <div className="plan-action-dropdown-btn" onClick={toggleMenu}>
        <i className="ti ti-dots-vertical"></i>
      </div>
      {menuIsOpen &&
        createPortal(
          <div
            ref={portalRef}
            className="plan-action-menu-portal"
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
            }}
          >
            <MenuComponent
              ref={menuRef}
              items={menuItems}
              orientation="Vertical"
              select={handleMenuSelect}
              cssClass="plan-action-menu"
              showItemOnClick={false}
            />
          </div>,
          document.body,
        )}
    </div>
  )
}

export default PlanActionMenuButton
